import supabase from './supabaseClient';

export interface ProcessFromAPI {
  id: string;
  user_id: string;
  job_id: string;
  job_type: 'runpod' | 'local';
  name: string;
  description?: string;
  status: 'pending' | 'in_queue' | 'in_progress' | 'running' | 'completed' | 'failed' | 'cancelled' | 'paused';
  project_id?: string;
  model_id?: string;
  runpod_job_id?: string;
  runpod_endpoint_id?: string;
  training_config?: {
    modelName?: string;
    model_name?: string;
    description?: string;
    gpuType?: string;
  };
  project_path?: string;
  backend_url?: string;
  current_epoch?: number;
  total_epochs?: number;
  progress_percentage?: number;
  train_loss?: number;
  val_loss?: number;
  training_progress_data?: any[];
  model_structure?: any;
  model_evaluation_info?: any;
  error_message?: string;
  error_details?: any;
  metadata?: any;
  created_at: string;
  started_at?: string;
  updated_at: string;
  completed_at?: string;
  cancelled_at?: string;
}

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Fetch all training processes for the current user from Supabase.
 * Returns only RunPod jobs (local jobs are ephemeral).
 */
export async function fetchUserProcesses(): Promise<ProcessFromAPI[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}/api/storage/processes`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch processes' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const processes: ProcessFromAPI[] = await response.json();
    return processes;
  } catch (error) {
    console.error('[ProcessService] Error fetching processes:', error);
    throw error;
  }
}

/**
 * Convert a process from API format to TrainingProcess format used by ProcessesContext.
 */
export function convertProcessToTrainingProcess(process: ProcessFromAPI): any {
  return {
    id: `process_${process.job_id}`,
    name: process.name,
    status: mapStatusToTrainingStatus(process.status),
    progress: process.progress_percentage || 0,
    startTime: process.started_at 
      ? new Date(process.started_at).toLocaleString() 
      : new Date(process.created_at).toLocaleString(),
    job_id: process.job_id,
    training_type: process.job_type,
    config: {
      modelName: process.training_config?.modelName || process.training_config?.model_name || process.name,
      description: process.description || process.training_config?.description,
      gpuType: process.training_config?.gpuType,
    },
    trainingProgressData: [], // Don't load full progress data from DB (too large)
    modelStructure: process.model_structure || null,
    modelEvaluationInfo: process.model_evaluation_info || null,
    lastTrainingError: process.error_message || null,
    epoch: process.current_epoch || 0,
    totalEpochs: process.total_epochs || 100,
    train_loss: process.train_loss || 0,
    val_loss: process.val_loss || 0,
  };
}

/**
 * Map database status to TrainingProcess status.
 */
function mapStatusToTrainingStatus(status: string): 'running' | 'completed' | 'failed' | 'paused' | 'cancelled' {
  switch (status) {
    case 'running':
    case 'in_progress':
    case 'in_queue':
      return 'running';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'paused':
      return 'paused';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'running'; // Default to running for unknown statuses
  }
}

